// src/utils/fieldConfigHelpers.ts
import React from 'react';
import { ColDef } from 'ag-grid-community';
import { FormFieldConfig } from '../types/formField.types';
import * as Yup from 'yup';
import { Tooltip, Typography } from 'antd';
import { optionsField } from './formFields';
const { Text } = Typography;

export function getFormFields(config: FormFieldConfig[]) {
  return config
    .filter(field =>
      field.type && (
        !field.showOn || field.showOn.some(v => v !== 'table')
      )
    )
    .map(({
      name,
      label,
      type,
      required,
      showOn,
      infoMessage,
      options,
      multiple,
      accept,
      allowedExtensions,
      maxFileSizeMB,
      xs,
      sm,
      md,
      lg,
      xl,
    }) => ({
      name,
      label,
      type,
      required,
      showOn: showOn || ['create', 'edit', 'view'],
      infoMessage,
      ...(typeof xs === 'number' ? { xs } : {}),
      ...(typeof sm === 'number' ? { sm } : {}),
      ...(typeof md === 'number' ? { md } : {}),
      ...(typeof lg === 'number' ? { lg } : {}),
      ...(typeof xl === 'number' ? { xl } : {}),
      ...(options ? { options } : {}), // Include `options` only if it exists
      ...(typeof multiple !== 'undefined' ? { multiple } : {}),
      ...(accept ? { accept } : {}),
      ...(allowedExtensions ? { allowedExtensions } : {}),
      ...(typeof maxFileSizeMB === 'number' ? { maxFileSizeMB } : {}),
    }));
}
const cellEditHandleYupValidation = (params: any, yup: any): boolean => {
  const { newValue, data, colDef } = params; // Extract relevant properties
  const schema = yup;

  if (!schema) {
    // No validation schema, directly update the value
    data[colDef.field] = newValue;
    return true;
  }

  const isValid = isYupValid(newValue, yup);
  if (isValid.status) {
    // Update the value in the grid's data if validation passes
    data[colDef.field] = newValue;
    return true;
  } else {
    // Show an alert if validation fails
    alert(isValid.message || 'Invalid value');
    return false;
  }
};

export function getTableColumns(config: FormFieldConfig[]): ColDef[] {
  return config
    .filter(field =>
      !field.showOn || field.showOn.includes('table')
    )
    .map(({ label, name, table = {} }) => ({
      headerName: label,
      field: name,
      ...table, // Include table-specific configurations
    }));
}

export function getExcelColumns(config: FormFieldConfig[]): ColDef[] {
  return config
    .filter(field =>
      !field.showOn || field.showOn.includes('create')
    )
    .map(({ label, name, yup, table = {}, type, options, example }) => {
      const isSelect = optionsField.includes(type || "");
      const validValues = options?.map(opt => opt.label) || []; 
      const finalYup = isSelect && validValues
        ? (yup ?? Yup.mixed()).oneOf([...validValues,""], `Please choose a valid option from the list.`)
        : yup;

      return {
        headerName: label,
        field: name,
        example: example || "",
        editable: (params: any) => params.data?.status !== 'Inactive', // Editable logic for Excel
        valueSetter: (params: any) => cellEditHandleYupValidation(params, finalYup), // Value setter for Excel
        cellRenderer: (params: any) => {
          const { value, data } = params;
          if (data?.excelDeleted) {
            return <Text delete mark>{value}</Text>; // Special styling for inactive rows
          }
          const isValid = isYupValid(value, finalYup);
          if (finalYup && !isValid.status) {
            return (
              <Tooltip title={isValid.message} color="red">
                <span className="inline-block w-full min-h-[1rem]">
                  {value || <span className="text-gray-400"></span>}
                </span>
              </Tooltip>
            ); // Invalid value
          }
          return value; // Valid value
        },
        cellClassRules: {
          'bg-red-50 border border-red-400 text-red-700': (params) => {
            const value = params.value;
            return finalYup && !isYupValid(value, finalYup).status;
          }
        },
        cellEditor: isSelect ? 'agSelectCellEditor' : 'agTextCellEditor', // Cell editor type
        cellEditorParams: { values: validValues }, // Cell editor params
        ...table,
      };
    });
}

export function getValidationSchema(config: FormFieldConfig[]): Yup.ObjectSchema<any> {
  const shape: Record<string, Yup.AnySchema> = {};
  for (const field of config) {
    if (field.yup) {
      shape[field.name] = field.yup;
    }
  }
  return Yup.object().shape(shape).defined().noUnknown();
}

export function getInitialValues(config: FormFieldConfig[], data?: any): Record<string, any> {
  const initialValues: Record<string, any> = {};
  for (const field of config) {
    if (typeof field.getInitialValue === 'function') {
      initialValues[field.name] = field.getInitialValue(data);
    }
  }
  return initialValues;
}

export const isYupValid = (value: any, schema: any): any => {
  if (!schema) return { status: true, message: "" }; // no validation attached
  try {
    schema.validateSync(value); // Validate the value
    return { status: true, message: "" }; // Return true if valid
  } catch (error: any) {
    return { status: false, message: error.message || 'Invalid value' }; // Return false if invalid
  }
};
